import { useEffect, useRef } from 'react'

import { useRouter } from 'next/router'

import {
	useNewNextButtonDisabled,
	useNewNextQuestion,
	useNewPreviousQuestion,
	useNewSurvey,
	useNewSurveyState
} from '@context/new-survey/useNewSurvey'
import { clearSurveySession } from '@lib/hooks/useSurveySession'
import { trackGetStartedEvent } from '@lib/analytics/get-started-tracking'
import { upgradeClaritySession } from '@lib/analytics/clarity'

import commonTextNodes from '@data/i18n/common.json'

import Logo from '@components/global/Logo'

import type { AnnouncementConfig } from '@lib/new-survey/get-new-survey'

import ScrollOverflowIndicator from '@components/scroll-indicators/ScrollOverflowIndicator'

import InlineMultiSelect from './InlineMultiSelect'
import MultiSelect from './MultiSelect'
import RadioSelect from './RadioSelect'
import RangeSlider from './RangeSlider'

type NewSurveyParams = {
	callBack?: (() => void) | null
	customFinishText?: string
	onBack?: () => void
	announcement?: AnnouncementConfig | null
	totalSteps?: number
}
export default function NewSurvey({
	callBack = null,
	customFinishText,
	onBack,
	announcement,
	totalSteps
}: NewSurveyParams) {
	const router = useRouter()

	// Survey questions
	const { surveyQuestions } = useNewSurvey() || {}

	const { currentIndex } = useNewSurveyState()
	const nextQuestion = useNewNextQuestion()
	const previousQuestion = useNewPreviousQuestion()
	const nextButtonDisabled = useNewNextButtonDisabled()

	const scrollRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		scrollRef.current?.scrollTo(0, 0)
	}, [currentIndex])

	const totalQuestions = surveyQuestions?.length || 0
	const displayTotal = totalSteps ?? totalQuestions

	const handleNext = () => {
		const currentQuestion = surveyQuestions?.[currentIndex]
		const questionId = currentQuestion?.id
		const isLastQuestion =
			questionId === surveyQuestions?.[surveyQuestions.length - 1]?.id

		// Use stable question id (e.g. skin_tone, eye_color) so reordering doesn't break tracking
		if (questionId) {
			trackGetStartedEvent(`survey_${questionId}_answered`, { question_id: questionId })
		}

		if (isLastQuestion && !!callBack) {
			clearSurveySession()
			trackGetStartedEvent('survey_completed')
			upgradeClaritySession('get_started_survey_completed')
			callBack()
		} else {
			nextQuestion()
		}
	}

	const handlePrevious = () => {
		if (currentIndex === 0) {
			onBack?.()
		} else {
			previousQuestion()
		}
	}

	return (
		<div className="flex flex-col h-[100dvh] overflow-hidden bg-white md:bg-transparent">
			{/* Fixed top: Logo + announcement + progress + previous */}
			<div className="flex-shrink-0 bg-white">
				<div className="container">
					{/* Logo bar */}
					<div className="flex justify-center py-3 md:py-5">
						<button
							type="button"
							aria-label="Birchbox Home"
							className="cursor-pointer"
							onClick={() => {
								if (typeof window !== 'undefined' && window.history.length > 1) {
									router.back()
								} else {
									router.push('/')
								}
							}}
						>
							<Logo className="w-28 md:w-32 fill-bbx-style" />
						</button>
					</div>
				</div>

				{/* Announcement bar */}
				{/* {announcement && (
					<div
						className="bg-bbx-glimmer text-white text-center text-base md:text-lg py-2 px-4 font-geo-sans font-normal"
						dangerouslySetInnerHTML={{ __html: announcement.text }}
					/>
				)} */}

				<div>
					{/* Progress row + Previous (inline on desktop) */}
					<div className="flex flex-wrap items-center gap-2 px-4 pt-2 pb-0 md:px-12 md:pt-6 max-w-4xl mx-auto w-full">
						{/* Previous button */}
						<div className="order-2 w-full pt-1 pb-0 md:order-first md:w-auto md:pt-0 md:pb-0 md:-ml-12 md:mr-12">
							{currentIndex > 0 ? (
								<button
									type="button"
									className="text-base font-sans font-light text-bbx-style transition-colors underline"
									onClick={handlePrevious}
								>
									{commonTextNodes.survey.back}
								</button>
							) : (
								<div className="h-5 md:hidden" />
							)}
						</div>
						{/* Step counter */}
						<span className="text-sm text-bbx-style whitespace-nowrap order-1 md:order-2">
							{currentIndex + 1} of {displayTotal}
						</span>
						{/* Progress bar */}
						<div className="relative flex-1 h-4 overflow-hidden bg-bbx-glimmer-light border-[3px] border-bbx-glimmer-light order-1 md:order-3">
							<div
								className="absolute inset-y-0 left-0 h-2.5 transition-all duration-[800ms] ease-in-out bg-bbx-glimmer"
								style={{ width: `${((currentIndex + 1) / displayTotal) * 100}%` }}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Scrollable content area */}
			<div className="relative flex-1 min-h-0 overflow-hidden bg-white">
				<div ref={scrollRef} className="h-full overflow-y-auto bg-white container">
					<div className="flex flex-col px-4 py-6 md:px-12 md:py-10 max-w-4xl mx-auto w-full">
						{Array.isArray(surveyQuestions) &&
							surveyQuestions.map((questionObj, i) => {
								if (questionObj?.type === 'radio_select') {
									return (
										<RadioSelect
											surveyQuestion={questionObj}
											isRequired={questionObj?.isRequired}
											index={i}
											key={questionObj?.id}
										/>
									)
								} else if (questionObj?.type === 'range_slider') {
									return (
										<RangeSlider
											surveyQuestion={questionObj}
											isRequired={questionObj?.isRequired}
											index={i}
											key={questionObj?.id}
										/>
									)
								} else if (questionObj?.type === 'multiselect') {
									return (
										<MultiSelect
											surveyQuestion={questionObj}
											isRequired={questionObj?.isRequired}
											index={i}
											key={questionObj?.id}
										/>
									)
								} else if (questionObj?.type === 'inline_multiselect') {
									return (
										<InlineMultiSelect
											surveyQuestion={questionObj}
											isRequired={questionObj?.isRequired}
											index={i}
											key={questionObj?.id}
										/>
									)
								}
								return null
							})}
					</div>
				</div>
				<ScrollOverflowIndicator scrollRef={scrollRef} showGradientFade showBouncingChevron />
			</div>

			{/* Fixed bottom: Next button */}
			<div className="flex-shrink-0 bg-white border-t border-gray-100 md:border-gray-300 md:shadow-[0_-4px_12px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]">
				<div className="px-4 py-2.5 md:py-4 md:px-12 max-w-md mx-auto w-full md:flex md:justify-center text-lg">
					<button
						key={currentIndex}
						type="button"
						className="button button-action btn-lg w-full md:min-w-[400px]"
						disabled={nextButtonDisabled}
						onClick={(e) => {
							e.currentTarget.blur()
							handleNext()
						}}
					>
						{surveyQuestions?.[currentIndex]?.id ===
						surveyQuestions?.[surveyQuestions.length - 1]?.id
							? customFinishText || commonTextNodes.survey.finish
							: commonTextNodes.survey.next}
					</button>
				</div>
			</div>
		</div>
	)
}
